import countries from "i18n-iso-countries"
import enCountryLocaleData from "i18n-iso-countries/langs/en.json"
import languages from "langs"
import moment from "moment"

countries.registerLocale(enCountryLocaleData)

export const trimRegionFromLocaleCode = (locale) => locale.split("-")[0]
export const languageCodeExists = (languageCode) =>
  languageCode ? languages.has("1", trimRegionFromLocaleCode(languageCode)) : false

/**
 * 🌐  — Load library and UI locale data for a supported language *
 * ? Supported languages need to be defined in webpack.config.prod.js (line: 244) `ContextReplacementPlugin`.
 * It should also be defined in the globals.js `SUPPORTED_LANGUAGES` const.
 *
 * Libraries included: `i18n-iso-countries`, `react-intl`, `moment`
 * @param {'en-gb' | 'da-dk' | 'pl-pl' | 'sv-se' | 'de-de' | 'es' | 'tr-tr' | 'zh' } localeCode - supported language code
 */

const notLanguages = ["es-cl", "es-es"]

export const loadLocaleSettings = (localeCode) => {
  const code = trimRegionFromLocaleCode(localeCode)
  const localeCodeForAntd =
    notLanguages.indexOf(localeCode) >= 0
      ? localeCode.split("-")[0]
      : localeCode
          .split("-")
          .map((value, index) => (index === 1 ? value.toUpperCase() : value))
          .join("_") // example: da-dk -> da_DK

  const antdLocaleData = import(
    `antd/lib/locale-provider/${localeCodeForAntd === "en" ? "en_GB" : localeCodeForAntd}.js`
  )
    .then((module) => module.default)
    .catch(() =>
      import("../locales/antd-fallback").then((module) => module.default(localeCodeForAntd))
    )
  const countriesData = import(`i18n-iso-countries/langs/${code}.json`).then((module) =>
    countries.registerLocale(module.default)
  )
  // moment.js locale fetch is a bit unique, so this helper covers edge cases
  const getMomentCodeName = (code) => {
    switch (code) {
      // for English locale there is no plain en.js in moment.js, so it falls back to British version
      case "en":
        return "en-gb"
      // for Chinese, we need to use the exact localeCode (with country code) since moment expects this format
      case "zh":
        return localeCode
      // for other languages, a trimmed (no country code) code is used to fetch moment locale
      default:
        return code
    }
  }

  const momentData = import(`moment/locale/${getMomentCodeName(code)}`).then(() =>
    moment.locale(code)
  )
  const timeFormatData = import(`@formatjs/intl-relativetimeformat/locale-data/${code}`)

  const languageLocale = import(`../locales/${localeCode}.json`).then((module) => module.default)

  return Promise.all([
    languageLocale, // application layer localization
    antdLocaleData, // locale data for antd
    countriesData, // country name localization
    timeFormatData,
    momentData, // cultural data formatting for momentjs
  ]).then((responses) => {
    const [locale, antdLocale] = responses
    return { locale, antdLocale } // returns language data
  })
}
